import React, { useState, useEffect, useCallback } from "react";
import styles from "./SessionsGroupList.module.sass";
import { useNavigate } from "react-router-dom"; 
import cn from "classnames";
import Card from "../../components/Card";
import Form from "../../components/Form";
import Icon from "../../components/Icon";

import Table from "./Table";
import Panel from "./Panel";

import { 
  getSessionGroups, 
  deleteSessionGroup, 
  duplicateSessionGroup,
  deleteMultipleSessionGroups,
  duplicateMultipleSessionGroups
} from "../../services/sessionGroup.service.js";

const SessionsGroupList = () => {
  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const [sessionGroups, setSessionGroups] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [noResults, setNoResults] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [selectedIds, setSelectedIds] = useState([]);
  
  // États pour les filtres
  const [filters, setFilters] = useState({
    sorting: "Plus récents"
  });

  // Gestion des sélections
  const handleSelectionChange = (sessionId, isSelected) => {
    console.log('handleSelectionChange called:', { sessionId, isSelected });
    setSelectedIds(prev => {
      console.log('Previous selectedIds:', prev);
      let newIds;
      if (isSelected) {
        // Éviter les doublons
        if (!prev.includes(sessionId)) {
          newIds = [...prev, sessionId];
        } else {
          newIds = prev;
        }
      } else {
        newIds = prev.filter(id => id !== sessionId);
      }
      console.log('New selectedIds:', newIds);
      return newIds;
    });
  };

  const clearSelection = () => {
    setSelectedIds([]);
  };

  // Suppression individuelle
  const handleDeleteSessionGroup = async (sessionGroupId) => {
    try {
      await deleteSessionGroup(sessionGroupId);
      setSessionGroups(prev => prev.filter(sessionGroup => sessionGroup.id !== sessionGroupId));
      setSelectedIds(prev => prev.filter(id => id !== sessionGroupId));
    } catch (error) {
      console.error('Erreur lors de la suppression du groupe de session:', error);
      throw error;
    }
  };

  // Duplication individuelle
  const handleDuplicateSessionGroup = async (sessionGroupId) => {
    try {
      const duplicatedSessionGroup = await duplicateSessionGroup(sessionGroupId);
      setSessionGroups(prev => [duplicatedSessionGroup, ...prev]);
    } catch (error) {
      console.error('Erreur lors de la duplication du groupe de session:', error);
      throw error;
    }
  };

  // Suppression en masse
  const handleDeleteSelected = async (sessionGroupIds) => {
    try {
      await deleteMultipleSessionGroups(sessionGroupIds);
      setSessionGroups(prev => prev.filter(sessionGroup => !sessionGroupIds.includes(sessionGroup.id)));
      setSelectedIds([]);
    } catch (error) {
      console.error('Erreur lors de la suppression en masse:', error);
      throw error;
    }
  };

  // Duplication en masse
  const handleDuplicateSelected = async (sessionGroupIds) => {
    try {
      const duplicatedSessionGroups = await duplicateMultipleSessionGroups(sessionGroupIds);
      setSessionGroups(prev => [...duplicatedSessionGroups, ...prev]);
      setSelectedIds([]);
    } catch (error) {
      console.error('Erreur lors de la duplication en masse:', error);
      throw error;
    }
  };

  const fetchSessionGroups = useCallback(async () => {
    setLoading(true);
    setError(null);
    setNoResults(false);

    try {
      // Construire les filtres
      let filterParts = [];
      
      // Filtre de recherche sur le titre uniquement (session_groups n'a que title)
      if (search.trim()) {
        filterParts.push(`title~'${search.toLowerCase().trim()}'`);
      }
      
      // Pas de filtre par statut car session_groups n'a pas ce champ
      
      const filterString = filterParts.length > 0 ? filterParts.join(' && ') : null;
      
      // Déterminer le tri
      let sortString = "-created"; // Par défaut : plus récents
      if (filters.sorting === "Plus anciens") {
        sortString = "+created";
      } else if (filters.sorting === "Alphabétique") {
        sortString = "+title";
      }

      const result = await getSessionGroups({
        page,
        perPage: 10,
        filter: filterString,
        sort: sortString
      });
      
      setSessionGroups(result.items || []); 
      setTotalPages(result.totalPages || 1); 
      setNoResults(result.items.length === 0 && !!search);

    } catch (err) {
      if (err.isAbort || (err.response && err.response.code === 0)) {
        console.warn('Request was autocancelled:', err.message);
      } else {
        console.error("Erreur lors du chargement des groupes de sessions:", err);
        setError("Impossible de charger les groupes de sessions");
        setSessionGroups([]);
        setTotalPages(1);
      }
    } finally {
      setLoading(false);
    }
  }, [page, search, filters]); 
  
  useEffect(() => {
    fetchSessionGroups();
  }, [fetchSessionGroups]);

  const handleSearch = (e) => {
    e?.preventDefault();
    setPage(1); 
    fetchSessionGroups();
  };

  // Gestionnaire pour les changements de filtres
  const handleFiltersChange = (newFilters) => {
    console.log('Nouveaux filtres appliqués:', newFilters);
    setFilters(newFilters);
    setPage(1); // Remettre à la première page lors du changement de filtres
  };

  const handlePageChange = (newPage) => {
    setPage(newPage);
  };

  return (
    <>
      <Card 
        className={styles.card} 
        title="Groupes" 
        classTitle={cn("title-blue", styles.title)} 
        classCardHead={styles.head} 
        head={
          <>
            <Form
              className={styles.form}
              value={search}
              setValue={setSearch}
              onSubmit={handleSearch}
              placeholder="Rechercher par groupe"
              type="text"
              name="search"
              icon="search"
            />
            <button 
              className={cn("button-stroke button-small", styles.button)} 
              onClick={() => navigate("/admin/session-groups/create")}
            >
              Créer
            </button>
          </>
        }
      >
        {error && <div className={styles.error}>{error}</div>}
        
        {loading ? (
          <div className={styles.loading}>Chargement...</div>
        ) : (
          <>
            <Table
              className={styles.table}
              sessionGroups={sessionGroups}
              loading={loading}
              error={error}
              noResults={noResults}
              page={page}
              totalPages={totalPages}
              onPageChange={handlePageChange}
              selectedIds={selectedIds}
              onSelectionChange={handleSelectionChange}
              onDeleteSessionGroup={handleDeleteSessionGroup}
              onDuplicateSessionGroup={handleDuplicateSessionGroup}
            />
            <div className={styles.foot}>
              <button 
                className={styles.arrow} 
                onClick={() => handlePageChange(Math.max(1, page - 1))} 
                disabled={page === 1}
              >
                <Icon name="arrow-left" size="20" />
              </button>
              <span className={styles.pagination}>
                Page {page} sur {totalPages}
              </span>
              <button 
                className={styles.arrow} 
                onClick={() => handlePageChange(Math.min(totalPages, page + 1))} 
                disabled={page === totalPages}
              >
                <Icon name="arrow-right" size="20" />
              </button>
            </div>
          </>
        )}
      </Card>
      <Panel 
        selectedSessionGroups={selectedIds}
        onDeleteSelected={handleDeleteSelected}
        onClearSelection={clearSelection}
        onDuplicateSelected={handleDuplicateSelected}
      />
    </>
  );
};

export default SessionsGroupList;